import { authFetch } from './authService';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

export interface Domain {
  code: string;
  name: string;
  description?: string;
  is_active: boolean;
}

export interface DomainInfo {
  name: string;
  version: string;
}

export interface DomainLabels {
  [fieldPath: string]: string;
}

class DomainsService {
  async getDomains(): Promise<string[]> {
    const response = await authFetch(`${API_URL}/api/v1/domains`);
    if (!response.ok) throw new Error('Failed to fetch domains');
    return response.json();
  }

  async getDomainInfo(domain: string): Promise<DomainInfo> {
    const response = await authFetch(`${API_URL}/api/v1/domains/${domain}/info`);
    if (!response.ok) throw new Error('Failed to fetch domain info');
    return response.json();
  }

  async getDomainLabels(domain: string, locale: string = 'fr'): Promise<DomainLabels> {
    const response = await authFetch(`${API_URL}/api/v1/domains/${domain}/labels?locale=${locale}`);
    if (!response.ok) throw new Error('Failed to fetch domain labels');
    return response.json();
  }

  async getDomainSchema(domain: string): Promise<any> {
    const response = await authFetch(`${API_URL}/api/v1/domains/${domain}/schema`);
    if (!response.ok) throw new Error('Failed to fetch domain schema');
    return response.json();
  }

  async getDomainOptions(): Promise<Array<{ value: string; label: string }>> {
    try {
      const response = await authFetch(`${API_URL}/api/v1/admin/domains`);
      if (!response.ok) throw new Error('Failed to fetch domains from admin endpoint');
      const domains = await response.json();
      return domains.map((domain: any) => ({
        value: domain.name,
        label: domain.name
      }));
    } catch (error) {
      console.error('Failed to load domains from admin endpoint:', error);
      const codes = await this.getDomains();
      return codes.map(code => ({ value: code, label: code }));
    }
  }

  async getActiveDomainOptions(): Promise<Array<{ value: string; label: string }>> {
    // PROD1.4.6 hotfix : utilise /tenant/me/domains (scopé tenant) au lieu
    // de /domains-admin (catalogue système). Le dropdown d'upload ne doit
    // afficher QUE les domaines activés pour le tenant courant — sinon un
    // analyste peut uploader vers un domaine sans config tenant et l'extraction
    // tourne avec des prompts/schemas non personnalisés.
    try {
      const response = await authFetch(`${API_URL}/api/v1/tenant/me/domains`);
      if (!response.ok) throw new Error('Failed to fetch tenant domains');
      const domains = await response.json();
      return domains.map((d: any) => ({
        value: d.domain_code,
        label: d.display_name || d.domain_code,
      }));
    } catch (error) {
      console.error('Failed to load tenant domains:', error);
      const codes = await this.getDomains();
      return codes.map(code => ({ value: code, label: code }));
    }
  }
}

export const domainsService = new DomainsService();
